import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import {
  CatalogRecord,
  ChillerCircuit,
  ChillerComponents,
  ChillerFields,
  FormValues,
  Lookups,
} from "./CatalogAdmin.types.tsx";
import CircuitCard from "./CircuitCard.tsx";
import CompressorModal from "./CompressorModal.tsx";
import FieldInput from "./FieldInput.tsx";
import {
  CENTRIFUGAL,
  MAX_CIRCUITS,
  emptyCircuit,
  failureMessages,
  labelOf,
  missingCompressors,
  problemOf,
  toFields,
  toFormValues,
  totalsOf,
} from "./model.tsx";
import { CHILLER_GENERAL, CHILLER_REFERENCES, REFERENCES, resourceSpec } from "./resources.tsx";

import {
  createRecord,
  getChillerComponents,
  updateRecord,
} from "../../services/catalog.services.tsx";

const SCALARS = [...CHILLER_GENERAL, ...CHILLER_REFERENCES];

const DATALISTS: (keyof ChillerComponents)[] = ["condensers", "fans", "evaporators", "coils"];

const isField = (key: string): boolean =>
  SCALARS.some((field) => field.name === key) || key === "circuits" || key.startsWith("circuits[");

interface ChillerFormProps {
  record: CatalogRecord<ChillerFields> | null;
  lookups: Lookups;
  onBack: () => void;
  onSaved: () => void;
  onCompressorCreated: () => void;
}

const ChillerForm: React.FC<ChillerFormProps> = ({
  record,
  lookups,
  onBack,
  onSaved,
  onCompressorCreated,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<FormValues>(() =>
    toFormValues(SCALARS, record?.fields ?? null)
  );
  const [inverter, setInverter] = useState<boolean>(Boolean(record?.fields.inverter));
  const [circuits, setCircuits] = useState<ChillerCircuit[]>(() =>
    record?.fields.circuits?.length ? record.fields.circuits : [emptyCircuit()]
  );
  const [components, setComponents] = useState<ChillerComponents | null>(null);
  const [creatingCompressor, setCreatingCompressor] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [messages, setMessages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getChillerComponents()
      .then(setComponents)
      .catch(() => setComponents(null));
  }, []);

  const technologyName =
    lookups.technologies?.find((item) => String(item.id) === values.technology_id)?.fields.name ??
    null;
  const centrifugal = technologyName === CENTRIFUGAL;
  const knownCompressors = (lookups.compressors ?? []).map((item) => String(item.fields.model));
  const missing = lookups.compressors
    ? missingCompressors(circuits, knownCompressors, technologyName)
    : [];
  const totals = totalsOf(circuits);
  const refrigerants = (lookups.refrigerants ?? []).map((item) => String(item.fields.name));

  const setCircuit = (index: number, circuit: ChillerCircuit) =>
    setCircuits(circuits.map((current, i) => (i === index ? circuit : current)));

  const errorsOf = (index: number) =>
    Object.entries(fieldErrors)
      .filter(([key]) => key.startsWith(`circuits[${index}]`))
      .flatMap(([, list]) => list);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);

    try {
      const body = { ...toFields(SCALARS, values), inverter, circuits };

      if (record) {
        await updateRecord("chillers", record.id, body);
      } else {
        await createRecord("chillers", body);
      }

      onSaved();
    } catch (error) {
      setFieldErrors(problemOf(error).errors);
      setMessages(failureMessages(error, isField, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Form onSubmit={submit}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="link" onClick={onBack}>
            {t("catalog.chiller.back")}
          </Button>
          <Button type="submit" variant="success" disabled={busy}>
            {t("catalog.form.save")}
          </Button>
        </div>

        {messages.map((message) => (
          <Alert key={message} variant="danger">
            {message}
          </Alert>
        ))}

        {missing.length > 0 && (
          <Alert variant="warning">
            <p>{t("catalog.chiller.missing", { models: missing.join(", ") })}</p>
            {missing.map((model) => (
              <Button
                key={model}
                size="sm"
                variant="outline-dark"
                className="me-2"
                onClick={() => setCreatingCompressor(model)}
              >
                {t("catalog.chiller.createCompressor", { model })}
              </Button>
            ))}
          </Alert>
        )}

        <h5>{t("catalog.chiller.general")}</h5>
        <Row>
          {CHILLER_GENERAL.map((field) => (
            <Col key={field.name} md={4}>
              <FieldInput
                scope="chiller"
                field={field}
                value={values[field.name] ?? ""}
                errors={fieldErrors[field.name]}
                onChange={(value) => setValues({ ...values, [field.name]: value })}
              />
            </Col>
          ))}
          <Col md={4} className="d-flex align-items-center mb-3">
            <Form.Check
              type="switch"
              id="chiller-inverter"
              label={t("catalog.fields.inverter")}
              checked={inverter}
              onChange={(event) => setInverter(event.target.checked)}
            />
          </Col>
        </Row>

        <h5>{t("catalog.chiller.references")}</h5>
        <Row>
          {CHILLER_REFERENCES.map((field) => {
            const resource = REFERENCES[field.name];

            return (
              <Col key={field.name} md={4}>
                <Form.Group className="mb-3" controlId={`chiller-${field.name}`}>
                  <Form.Label>{labelOf(field, t)}</Form.Label>
                  <Form.Select
                    required={field.required}
                    value={values[field.name] ?? ""}
                    isInvalid={Boolean(fieldErrors[field.name])}
                    onChange={(event) => setValues({ ...values, [field.name]: event.target.value })}
                  >
                    <option value="">{t("catalog.form.none")}</option>
                    {(lookups[resource] ?? []).map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {String(item.fields[resourceSpec(resource).searchField])}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors[field.name]?.join(" ")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            );
          })}
        </Row>

        <h5>{t("catalog.chiller.circuits")}</h5>
        {(fieldErrors.circuits ?? []).map((message) => (
          <Alert key={message} variant="danger">
            {message}
          </Alert>
        ))}
        {centrifugal && <Alert variant="info">{t("catalog.chiller.centrifugalNote")}</Alert>}
        <Row>
          {circuits.map((circuit, index) => (
            <Col key={index} lg={6}>
              <CircuitCard
                index={index}
                circuit={circuit}
                centrifugal={centrifugal}
                knownCompressors={knownCompressors}
                errors={errorsOf(index)}
                canRemove={circuits.length > 1}
                onChange={(next) => setCircuit(index, next)}
                onRemove={() => setCircuits(circuits.filter((_, i) => i !== index))}
              />
            </Col>
          ))}
        </Row>
        {circuits.length < MAX_CIRCUITS && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setCircuits([...circuits, emptyCircuit()])}
          >
            {t("catalog.chiller.addCircuit")}
          </Button>
        )}

        <div className="mt-3">
          <strong>{t("catalog.chiller.totals")}</strong>
          <ul className="mb-1">
            <li>
              {t("catalog.chiller.totalCompressors")}: {totals.compressors}
            </li>
            <li>
              {t("catalog.chiller.totalCondensers")}: {totals.condensers}
            </li>
            <li>
              {t("catalog.chiller.totalFans")}: {totals.fans}
            </li>
            <li>
              {t("catalog.chiller.totalEvaporators")}: {totals.evaporators}
            </li>
          </ul>
          <Form.Text>{t("catalog.chiller.totalsNote")}</Form.Text>
        </div>

        {DATALISTS.map((key) => (
          <datalist key={key} id={`chiller-${key}`}>
            {(components?.[key] ?? []).map((model) => (
              <option key={model} value={model} />
            ))}
          </datalist>
        ))}
      </Form>

      {creatingCompressor !== null && (
        <CompressorModal
          record={null}
          initialModel={creatingCompressor}
          refrigerants={refrigerants}
          onHide={() => setCreatingCompressor(null)}
          onSaved={() => {
            setCreatingCompressor(null);
            onCompressorCreated();
          }}
        />
      )}
    </>
  );
};

export default ChillerForm;
